import { Center, Flex, Grid, GridItem, Heading, Spinner, Text } from "@chakra-ui/react";

import PageHeader from "components/layout/PageHeader";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ConversationsList from "./elements/ConversationsList";
import CurrentConversation from "./elements/CurrentConversation";
import NewConversation from "./elements/NewConversation";
import { apiRequest } from "utils/api";
import { MessagesProvider } from "providers/messages";
import { toggleAccountType } from "utils/helpers";
import { AlertsContext } from "providers/alerts";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import { getErrorMessage } from "utils/errorHandler";

const formatParticipant = (user: any) => ({
  id: user.id,
  first_name: user.first_name,
  last_name: user.last_name,
  avatar: user.avatar,
  tagline: user.is_active
    ? user.type === "borrower"
      ? (user.company || {}).name
      : user.job_title
    : "Inactivated User",
  is_active: user.is_active,
});

function Messages() {
  const { id } = useParams();
  const { showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);

  const { search } = useLocation();
  const navigate = useNavigate();

  let params = useMemo(() => new URLSearchParams(search), [search]);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest("/supabase/conversations/select");
      setConversations(
        (response.conversations || []).map((conversation: any) => {
          return {
            ...conversation,
            participant:
              user.type === "borrower"
                ? formatParticipant(conversation.lender)
                : formatParticipant(conversation.borrower),
          };
        })
      );
      if (params.get("user") && !id) {
        let foundConversation = false;
        response.conversations.forEach((conversation: any) => {
          if (
            conversation.borrower.id === params.get("user") ||
            conversation.lender.id === params.get("user")
          ) {
            foundConversation = true;
            navigate(`/messages/${conversation.id}`);
          }
        });
        if (!foundConversation) {
          const { conversation } = await apiRequest("/supabase/conversations/insert", {
            [user.type]: user.id,
            [toggleAccountType(user.type)]: params.get("user"),
          });
          navigate(`/messages/${conversation.id}`);
        }
      }
    } catch (err) {
      showError({
        title: "Something went wrong.",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, params, showError, user]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return loading ? (
    <Center>
      <Spinner />
    </Center>
  ) : (
    <MessagesProvider user={user}>
      <>
        <PageHeader title="Messages" />
        <WidgetBox height="100%">
          <Grid gridTemplateColumns={{ md: "3fr 9fr" }} gap={4} height="100%">
            <GridItem>
              <Flex direction="column" gap={4}>
                <Flex alignItems="center" gap={4}>
                  <Heading size="md" flexGrow={1}>
                    Conversations
                  </Heading>
                  <NewConversation
                    loadConversations={() => loadConversations()}
                    exclude={conversations.map(convo => convo.participant.id)}
                  />
                </Flex>
                <ConversationsList conversations={conversations} />
              </Flex>
            </GridItem>
            <GridItem borderLeftColor="gray.50" borderLeftWidth="1px">
              <Outlet />
              {id ? (
                <CurrentConversation
                  conversation={conversations.find(conversation => conversation.id === id)}
                />
              ) : (
                <Center w="100%" h="100%">
                  <Text color="#515355" fontSize="14px">
                    Select a conversation to begin a chat with someone.
                  </Text>
                </Center>
              )}
            </GridItem>
          </Grid>
        </WidgetBox>
      </>
    </MessagesProvider>
  );
}

export default Messages;
